import axios, { AxiosRequestConfig } from 'axios';
import { postData } from './axiosUtils';

export const LOCAL_TOKEN_KEY = 'cmsWebApiAccessToken';
export const REFRESH_TOKEN_KEY = 'cmsWebApiRefreshToken';
export const AUTH_MODE_KEY = 'cmsAuthMode'; // 'local' | 'msal'
export const MFA_TOKEN_KEY = 'cmsMfaPendingToken';

type SessionEndHandler = (mode: string | null) => void | Promise<void>;

let sessionEndHandler: SessionEndHandler | null = null;
let endingSession = false;
let refreshInFlight: Promise<boolean> | null = null;

const readJwtExp = (token: string): number | null => {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
};

/** Login / MFA / password / refresh flows may return 401 without ending CMS session. */
export const isPublicAuthRequestUrl = (url?: string): boolean => {
  if (!url) return false;
  return /\/api\/auth\/(login|mfa|forgot-password|reset-password|refresh|logout)/i.test(
    url
  );
};

const authService = {
  login: (emailOrUserName: string, password: string) =>
    postData({
      url: '/api/auth/login',
      data: { emailOrUserName, password },
    }),
  verifyMfa: (mfaToken: string, method: string, code: string) =>
    postData({
      url: '/api/auth/mfa/verify',
      data: { mfaToken, method, code },
    }),
  sendEmailMfa: (mfaToken: string) =>
    postData({ url: '/api/auth/mfa/email/send', data: { mfaToken } }),
  forgotPassword: (email: string) =>
    postData({ url: '/api/auth/forgot-password', data: { email } }),
  resetPassword: (email: string, token: string, newPassword: string) =>
    postData({
      url: '/api/auth/reset-password',
      data: { email, token, newPassword },
    }),
  getProfile: () => axios.get('/api/auth/profile').then((r) => r.data),
  changePassword: (currentPassword: string, newPassword: string) =>
    postData({
      url: '/api/auth/change-password',
      data: { currentPassword, newPassword },
    }),
  mfaStatus: () => axios.get('/api/auth/mfa/status').then((r) => r.data),
  enableMfa: () => postData({ url: '/api/auth/mfa/enable', data: {} }),
  disableMfa: (password: string) =>
    postData({ url: '/api/auth/mfa/disable', data: { password } }),
  setupAuthenticator: () =>
    postData({ url: '/api/auth/mfa/authenticator/setup', data: {} }),
  confirmAuthenticator: (code: string) =>
    postData({
      url: '/api/auth/mfa/authenticator/confirm',
      data: { code },
    }),
  enableEmailMfa: () => postData({ url: '/api/auth/mfa/email/enable', data: {} }),
  disableEmailMfa: () =>
    postData({ url: '/api/auth/mfa/email/disable', data: {} }),
  disableAuthenticator: () =>
    postData({ url: '/api/auth/mfa/authenticator/disable', data: {} }),
  regenerateRecoveryCodes: (password: string) =>
    postData({
      url: '/api/auth/mfa/recovery-codes',
      data: { password },
    }),
  persistLocalSession: (accessToken: string, refreshToken?: string | null) => {
    endingSession = false;
    localStorage.setItem(LOCAL_TOKEN_KEY, accessToken);
    localStorage.setItem(AUTH_MODE_KEY, 'local');
    localStorage.removeItem(MFA_TOKEN_KEY);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  clearSession: () => {
    localStorage.removeItem(LOCAL_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_MODE_KEY);
    localStorage.removeItem(MFA_TOKEN_KEY);
  },
  isLocalAuth: () => localStorage.getItem(AUTH_MODE_KEY) === 'local',
  hasLocalToken: () => !!localStorage.getItem(LOCAL_TOKEN_KEY),
  hasRefreshToken: () => !!localStorage.getItem(REFRESH_TOKEN_KEY),
  /** True when JWT `exp` is past (default 5s skew). */
  isAccessTokenExpired: (skewMs = 5000) => {
    const token = localStorage.getItem(LOCAL_TOKEN_KEY);
    if (!token) return true;
    const exp = readJwtExp(token);
    if (exp == null) return false;
    return Date.now() >= exp * 1000 - skewMs;
  },
  /**
   * Rotate local refresh token → new access (+ refresh). Single-flight.
   * Returns false when refresh is impossible.
   */
  refreshSession: async (): Promise<boolean> => {
    if (!authService.isLocalAuth()) {
      return false;
    }
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return false;
    }
    if (refreshInFlight) {
      return refreshInFlight;
    }

    refreshInFlight = (async () => {
      try {
        const response = await axios.post(
          '/api/auth/refresh',
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );
        const data = response?.data;
        if (data?.accessToken) {
          authService.persistLocalSession(
            data.accessToken,
            data.refreshToken ?? refreshToken
          );
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();

    return refreshInFlight;
  },
  /** Best-effort server revoke of the stored refresh token. */
  revokeRefreshToken: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return;
    try {
      await axios.post('/api/auth/logout', { refreshToken });
    } catch {
      // ignore — local clear still happens
    }
  },
  registerSessionEndHandler: (handler: SessionEndHandler | null) => {
    sessionEndHandler = handler;
  },
  /**
   * Clears the stored CMS token and routes the user back to login.
   * Safe to call many times (single-flight).
   */
  endExpiredSession: async () => {
    if (endingSession) return;
    if (
      !localStorage.getItem(LOCAL_TOKEN_KEY) &&
      !localStorage.getItem(AUTH_MODE_KEY) &&
      !localStorage.getItem(REFRESH_TOKEN_KEY)
    ) {
      return;
    }
    endingSession = true;
    const mode = localStorage.getItem(AUTH_MODE_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    authService.clearSession();
    if (refreshToken && mode === 'local') {
      try {
        await axios.post('/api/auth/logout', { refreshToken });
      } catch {
        // ignore
      }
    }
    try {
      if (sessionEndHandler) {
        await sessionEndHandler(mode);
        return;
      }
    } catch (e) {
      console.error('Session end handler failed:', e);
    }
    window.location.hash = '#/';
    window.location.reload();
  },
};

export type RetriableAxiosConfig = AxiosRequestConfig & {
  _cmsRetryAfterRefresh?: boolean;
};

export default authService;
